import { OidcStandardClaims } from "oidc-client-ts";

export type OidcUsername = keyof Pick<
  OidcStandardClaims,
  "name" | "preferred_username" | "nickname"
>;

declare global {
  // ATTENTION: when adding a new environment variable, make sure to add it to the `generateEnvConfig` method below and
  // ensure it’s surfaced via Vite (see vite.config.ts `define`) or use import.meta.env if you switch to that pattern.
  interface Window {
    _env_?: {
      DISABLE_TENANT_PREFIL_FROM_DOMAIN: string;
      API_URL: string;
      STELLAR_EXPERT_URL: string;
      HORIZON_URL: string;
      RPC_ENABLED: boolean;
      RECAPTCHA_SITE_KEY: string;
      SINGLE_TENANT_MODE: boolean;

      USE_SSO?: boolean;
      OIDC_AUTHORITY?: string;
      OIDC_CLIENT_ID?: string;
      OIDC_REDIRECT_URI?: string;
      OIDC_SCOPE?: string;
      OIDC_USERNAME_MAPPING?: OidcUsername;
    };
  }
}

const WINDOW_ENV_PATH = "/settings/env-config.js";

const toBoolean = (value: unknown): boolean => value === true || value === "true";
type WindowEnv = NonNullable<Window["_env_"]>;

const generateEnvConfig = async () => {
  if (process?.env?.REACT_APP_DISABLE_WINDOW_ENV !== "true") {
    try {
      const response = await fetch(WINDOW_ENV_PATH);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${WINDOW_ENV_PATH}: ${response.status}`);
      }

      const text = await response.text();
      const script = new Function(text);
      script.apply(null);
    } catch (error) {
      console.error("Unable to load runtime environment config", error);
    }
  }

  const windowEnv: Partial<WindowEnv> = window?._env_ || {};
  const singleTenantMode = toBoolean(
    process?.env?.REACT_APP_SINGLE_TENANT_MODE ?? windowEnv.SINGLE_TENANT_MODE,
  );
  const apiUrlFromConfig = process?.env?.REACT_APP_API_URL || windowEnv.API_URL;
  const API_URL =
    !singleTenantMode && typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : apiUrlFromConfig;

  return {
    API_URL,
    DISABLE_TENANT_PREFIL_FROM_DOMAIN:
      process?.env?.REACT_APP_DISABLE_TENANT_PREFIL_FROM_DOMAIN ||
      windowEnv.DISABLE_TENANT_PREFIL_FROM_DOMAIN,
    STELLAR_EXPERT_URL: process?.env?.REACT_APP_STELLAR_EXPERT_URL || windowEnv.STELLAR_EXPERT_URL,
    HORIZON_URL: process?.env?.REACT_APP_HORIZON_URL || windowEnv.HORIZON_URL,
    RPC_ENABLED: toBoolean(process?.env?.REACT_APP_RPC_ENABLED ?? windowEnv.RPC_ENABLED),
    RECAPTCHA_SITE_KEY: process?.env?.REACT_APP_RECAPTCHA_SITE_KEY || windowEnv.RECAPTCHA_SITE_KEY,
    SINGLE_TENANT_MODE: singleTenantMode,
    USE_SSO: toBoolean(process?.env?.REACT_APP_USE_SSO ?? windowEnv.USE_SSO),
    OIDC_AUTHORITY: process?.env?.REACT_APP_OIDC_AUTHORITY || windowEnv.OIDC_AUTHORITY,
    OIDC_CLIENT_ID: process?.env?.REACT_APP_OIDC_CLIENT_ID || windowEnv.OIDC_CLIENT_ID,
    OIDC_REDIRECT_URI: process?.env?.REACT_APP_OIDC_REDIRECT_URI || windowEnv.OIDC_REDIRECT_URI,
    OIDC_SCOPE: process?.env?.REACT_APP_OIDC_SCOPE || windowEnv.OIDC_SCOPE,
    OIDC_USERNAME_MAPPING:
      ((process?.env?.REACT_APP_OIDC_USERNAME_MAPPING ||
        windowEnv.OIDC_USERNAME_MAPPING) as OidcUsername) || undefined,
  };
};

export const {
  DISABLE_TENANT_PREFIL_FROM_DOMAIN,
  API_URL,
  STELLAR_EXPERT_URL,
  HORIZON_URL,
  RPC_ENABLED,
  RECAPTCHA_SITE_KEY,
  SINGLE_TENANT_MODE,
  USE_SSO,
  OIDC_AUTHORITY,
  OIDC_CLIENT_ID,
  OIDC_REDIRECT_URI,
  OIDC_SCOPE,
  OIDC_USERNAME_MAPPING,
} = await generateEnvConfig();
